/**
 * @fileoverview Tab-based lazy loading initializers for dealer configuration page.
 *
 * This module implements a performance optimization by deferring data loading until
 * users navigate to specific tabs. Each tab has a dedicated initializer that loads
 * only the data required for that tab.
 *
 * Architecture:
 * - Each tab exports an initializer function (e.g., salesInitializer, partsInitializer)
 * - Helper functions handle shared data across multiple tabs (predefined messages, clipping)
 * - A visitedTabsSet tracks which tabs have been initialized to prevent redundant loads
 *
 * @module modules/dealerConfiguration/sagas/tabs.bootloader
 * @see {@link module:modules/dealerConfiguration/sagas/index} - Main orchestrator
 */

import { put, call, select, all } from 'redux-saga/effects';

import { MAX_RECORD } from 'constants/Global';
import { putAndWait } from 'modules/app/sagas';

import { getAuditModelsSelect, initializeAuditModelsSelector } from 'modules/auditModels/actions/select';
import { getSelectedAuditModels } from 'modules/auditModels/selectors/config';
import { getParams, isInitialized } from 'modules/auditModels/selectors/select';
import { fetchBrandingTemplatesRequest } from 'modules/brandingTemplates/actions';
import { fetchChecklistsSelect } from 'modules/checklists/actions/dashboard';
import { getPredefinedMessages } from 'modules/dealers/actions';

import { getSelfServiceConfig } from 'modules/dealers/dealerSagas';
import { IntegrationTypeEnum } from 'modules/global/typing/enums/integrations.enum';
import { fetchGuidelines, findByDealerOrGroup } from 'modules/guidelines/actions';
import { getParams as getGuidelinesParams } from 'modules/guidelines/selectors/ui/dashboard/dealer';
import { getKiosksByPaymentTypes } from 'modules/kiosks/actions';
import { KioskPaymentType } from 'modules/kiosks/constants';
import { fetchAvailableMessagingAccounts } from 'modules/messagingAccounts/actions';
import { fetchTemplatesFOByDealerRequest } from 'modules/templates/actions';
import { getCurrentUserIsAdmin } from 'modules/users/selectors/currentUser';
import {
  getSelectedDealer,
  getSelectedDealerId,
  getSelectedDealerIntegrations,
  getConfigurationChecklistScope,
} from 'selectors/dealerSelectors';

import { uniq } from 'utils/ObjectUtils';

import { DEALER_TAB_CHECKLIST_TYPES } from '../constants';
import { fetchDmsProviders, fetchWhatsAppAccounts } from '../integrations/actions';
import { fetchDefaultClippingMedia } from '../sales/actions';
import { getDefaultClippingMedia } from '../sales/selectors/clipping';

/**
 * Loads predefined messages for the selected dealer (shared across multiple tabs).
 *
 * This helper ensures predefined messages are loaded only once, even when navigating
 * between tabs that need them. It checks the visitedTabsSet to see if any tab that
 * uses predefined messages has already been visited.
 *
 * Tabs using predefined messages: sales, afterSales, parts, audit, keyDeposit
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Set of tab keys that have been initialized
 * @yields {Effect} Redux action to fetch predefined messages
 */
function* initializePredefinedMessage(visitedTabsSet) {
  const PREDEFINED_MGS_TABS = ['sales', 'afterSales', 'parts', 'audit', 'keyDeposit'];
  const alreadyInitializePredefinedMessages = PREDEFINED_MGS_TABS.some(tab => visitedTabsSet.has(tab));
  if (!alreadyInitializePredefinedMessages) {
    const dealerId = yield select(getSelectedDealerId);
    yield put(getPredefinedMessages(dealerId));
  }
}

/**
 * Loads default clipping media (shared between sales and parts tabs).
 *
 * Clipping media are default media assets used in sales and parts configurations.
 * This helper ensures they're loaded only once, even when navigating between tabs.
 * It performs an additional check to see if media are already in Redux state.
 *
 * Tabs using clipping: sales, parts
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Set of tab keys that have been initialized
 * @yields {Effect} Redux action to fetch default clipping media (if not already loaded)
 */
function* initializeClipping(visitedTabsSet) {
  const CLIPPING_TABS = ['sales', 'parts'];
  const alreadyInitializeClipping = CLIPPING_TABS.some(tab => visitedTabsSet.has(tab));

  if (!alreadyInitializeClipping) {
    const defaultClippingMedias = yield select(getDefaultClippingMedia);
    if (defaultClippingMedias.length === 0) {
      yield put(fetchDefaultClippingMedia());
    }
  }
}

/**
 * Loads checklists specific to a dealer configuration tab.
 *
 * Each tab (sales, parts, afterSales, selfService) has:
 * 1. Specific checklist IDs configured for the dealer/group
 * 2. A default checklist type (ACCESSORY, PARTS, WORK, UPSELLING)
 *
 * This function fetches both sets of checklists and waits for completion.
 *
 * @generator
 * @param {string} tabKey - The tab key (e.g., 'sales', 'parts', 'afterSales', 'selfService')
 * @yields {Effect} Redux actions to fetch checklists by IDs and by type
 */
function* initializeChecklistsTab(tabKey) {
  const dealerId = yield select(getSelectedDealerId);
  const checklistIds = yield select(state => getConfigurationChecklistScope(state, tabKey));
  const checklistType = DEALER_TAB_CHECKLIST_TYPES[tabKey];

  const selectId = `${checklistType}-${dealerId}`;

  yield putAndWait(fetchChecklistsSelect({ dealerId, ids: checklistIds, size: checklistIds.length }));
  yield putAndWait(fetchChecklistsSelect({ dealerId, types: checklistType }, { selectId }));
}

/**
 * Loads guidelines configured for the dealer and its group (used in sales tab).
 *
 * Guidelines are vehicle inspection checklists/workflows. This function:
 * 1. Fetches specific guidelines selected at dealer and group level
 * 2. Loads all available guidelines for the dealer/group for the select dropdown
 *
 * @generator
 * @yields {Effect} Redux actions to fetch specific guidelines and dealer/group guidelines list
 */
function* preloadDealerGuidelines() {
  const dealer = yield select(getSelectedDealer);

  const selectedGroupGuideline = dealer.group?.config?.selectedGuidelines ?? {};
  const selectedDealerGuideline = dealer.config.sales?.selectedGuidelines ?? {};
  const guidelineIds = uniq(
    [...Object.values(selectedGroupGuideline), ...Object.values(selectedDealerGuideline)].flat()
  );

  if (guidelineIds.length > 0) {
    yield put(fetchGuidelines({ guidelineIds, size: MAX_RECORD }));
  }

  const { language: _language, ...dealerParams } = yield select(getGuidelinesParams);
  yield put(findByDealerOrGroup(dealerParams));
}

/**
 * Loads audit models for the audit configuration tab.
 *
 * Audit models are templates for vehicle audits. This function:
 * 1. Initializes a selector token if needed (prevents duplicate initializations)
 * 2. Fetches default audit models configured for the dealer
 * 3. Uses a paginated select pattern with specific fields for performance
 *
 * @generator
 * @param {string} dealerId - The dealer UUID
 * @yields {Effect} Redux actions to initialize selector and fetch audit models
 */
function* preloadAuditModels(dealerId) {
  const token = 'dealerConfig-audit-auditModelsSelect';
  const auditModelIds = yield select(getSelectedAuditModels);

  if (auditModelIds?.length > 0) {
    const isInit = yield select(state => isInitialized(state, token));
    if (!isInit) {
      yield put(initializeAuditModelsSelector(token));
    }

    const params = yield select(state => getParams(state, token));
    yield put(
      getAuditModelsSelect(
        token,
        {
          ...params,
          dealerId,
          fields: 'id,title,auditModelId',
          auditModelIds,
          size: MAX_RECORD,
          sort: ['title,asc'],
        },
        { shouldUpdate: true }
      )
    );
  }
}

/**
 * Loads the SMS accounts the dealer can pick, and its WhatsApp integration accounts if the dealer has
 * WhatsApp integration enabled.
 *
 * Used in integration tab to configure SMS and WhatsApp messaging providers.
 *
 * @generator
 * @param {string} dealerId - The dealer UUID
 * @yields {Effect} Redux actions to fetch SMS accounts and WhatsApp accounts (conditional)
 */
function* preloadSmsProvider(dealerId) {
  const integrations = yield select(getSelectedDealerIntegrations);

  yield put(fetchAvailableMessagingAccounts({ dealerId }));

  if (integrations.includes(IntegrationTypeEnum.WHATSAPP)) {
    yield put(fetchWhatsAppAccounts(dealerId, {}));
  }
}

/**
 * Initializes the Administration tab.
 *
 * Loads kiosks with Bezahl payment type for dealer management.
 * Only admin users can access this tab and see kiosk data.
 *
 * **Data loaded:**
 * - Kiosks with BEZAHL payment type (admin only)
 *
 * @generator
 * @yields {Effect} Redux action to fetch kiosks (if user is admin)
 */
function* administrationInitializer() {
  const dealerId = yield select(getSelectedDealerId);
  const isAdmin = yield select(getCurrentUserIsAdmin);

  if (isAdmin) {
    yield putAndWait(
      getKiosksByPaymentTypes(dealerId, { paymentTypes: [KioskPaymentType.BEZAHL], status: ['ACTIVE', 'INACTIVE'] })
    );
  }
}

/**
 * Initializes the After Sales tab.
 *
 * Loads data for after-sales service configuration (work orders, interventions).
 *
 * **Data loaded:**
 * - Predefined messages (shared helper)
 * - After-sales checklists (WORK type + configured checklists)
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Tracks visited tabs to avoid redundant loads
 * @yields {Effect} Parallel Redux actions for predefined messages and checklists
 */
function* afterSalesInitializer(visitedTabsSet) {
  yield all([call(initializePredefinedMessage, visitedTabsSet), call(initializeChecklistsTab, 'afterSales')]);
}

/**
 * Initializes the Self Service tab.
 *
 * Loads configuration for self-service kiosks and customer portals.
 *
 * **Data loaded:**
 * - Self-service configuration (available types, public links, etc.)
 * - Self-service checklists (UPSELLING type + configured checklists)
 *
 * @generator
 * @yields {Effect} Parallel Redux actions for self-service config and checklists
 */
function* selfServiceInitializer() {
  const dealerId = yield select(getSelectedDealerId);

  yield all([call(getSelfServiceConfig, dealerId), call(initializeChecklistsTab, 'selfService')]);
}

/**
 * Initializes the Parts tab.
 *
 * Loads data for parts sales configuration (accessories, tire sales, etc.).
 *
 * **Data loaded:**
 * - Default clipping media (shared helper)
 * - Predefined messages (shared helper)
 * - Parts checklists (PARTS type + configured checklists)
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Tracks visited tabs to avoid redundant loads
 * @yields {Effect} Parallel Redux actions for clipping, messages, and checklists
 */
function* partsInitializer(visitedTabsSet) {
  yield all([
    call(initializeClipping, visitedTabsSet),
    call(initializePredefinedMessage, visitedTabsSet),
    call(initializeChecklistsTab, 'parts'),
  ]);
}

/**
 * Initializes the Sales tab.
 *
 * Loads data for vehicle sales configuration (offers, guidelines, etc.).
 *
 * **Data loaded:**
 * - Guidelines (dealer/group configured + available list)
 * - Default clipping media (shared helper)
 * - Predefined messages (shared helper)
 * - Sales checklists (ACCESSORY type + configured checklists)
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Tracks visited tabs to avoid redundant loads
 * @yields {Effect} Parallel Redux actions for guidelines, clipping, messages, and checklists
 */
function* salesInitializer(visitedTabsSet) {
  const dealerId = yield select(getSelectedDealerId);

  yield all([
    call(preloadDealerGuidelines, dealerId),
    call(initializeClipping, visitedTabsSet),
    call(initializePredefinedMessage, visitedTabsSet),
    call(initializeChecklistsTab, 'sales'),
  ]);
}

/**
 * Initializes the Audit tab.
 *
 * Loads data for vehicle audit configuration (audit models, templates).
 *
 * **Data loaded:**
 * - Audit models (default models configured for dealer)
 * - Predefined messages (shared helper)
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Tracks visited tabs to avoid redundant loads
 * @yields {Effect} Parallel Redux actions for audit models and predefined messages
 */
function* auditModelInitializer(visitedTabsSet) {
  const dealerId = yield select(getSelectedDealerId);

  yield all([call(preloadAuditModels, dealerId), call(initializePredefinedMessage, visitedTabsSet)]);
}

/**
 * Initializes the Key Deposit tab.
 *
 * Loads data for key deposit/locker configuration.
 *
 * **Data loaded:**
 * - Predefined messages (shared helper)
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Tracks visited tabs to avoid redundant loads
 * @yields {Effect} Redux action for predefined messages
 */
function* keyDepositInitializer(visitedTabsSet) {
  yield call(initializePredefinedMessage, visitedTabsSet);
}

/**
 * Initializes the Customisation tab.
 *
 * Loads templates for customizing dealer-specific document templates and branding.
 *
 * **Data loaded:**
 * - Dealer-specific document templates (invoices, reports, etc.)
 * - Branding templates (logos, colors, layouts)
 *
 * @generator
 * @yields {Effect} Parallel Redux actions for dealer templates and branding templates
 */
function* customisationInitializer() {
  const dealerId = yield select(getSelectedDealerId);

  yield all([
    put(fetchBrandingTemplatesRequest({ sort: 'folderS3Key,asc', size: MAX_RECORD })),
    put(fetchTemplatesFOByDealerRequest({ dealerId, sort: 'name,asc', size: MAX_RECORD })),
  ]);
}

/**
 * Initializes the Integration tab.
 *
 * Loads data for external system integrations (API tokens, WhatsApp, etc.).
 *
 * **Data loaded:**
 * - Integration token (admin only)
 * - SMS accounts available to the dealer
 * - WhatsApp accounts (if WhatsApp integration enabled)
 * - DMS providers
 *
 * @generator
 * @yields {Effect} Parallel Redux actions for integration token and SMS/WhatsApp providers
 */
function* integrationInitializer() {
  const dealerId = yield select(getSelectedDealerId);

  yield all([call(preloadSmsProvider, dealerId), put(fetchDmsProviders(dealerId, { sort: 'name,asc', size: MAX_RECORD }))]);
}

/**
 * Tab initializer function dictionary.
 *
 * Maps dealer configuration tab keys to their corresponding initializer functions.
 * When a user navigates to a tab, the main saga looks up the initializer here
 * and calls it to load the necessary data for that tab.
 *
 * **Tab Keys:**
 * - `general` - No initializer (base dealer data loaded by main saga)
 * - `administration` - Kiosks management
 * - `afterSales` - After-sales service configuration
 * - `selfService` - Self-service kiosks and portals
 * - `parts` - Parts sales configuration
 * - `sales` - Vehicle sales configuration
 * - `audit` - Vehicle audit configuration
 * - `keyDeposit` - Key deposit/locker configuration
 * - `customisation` - Document templates and branding
 * - `integration` - External system integrations
 *
 * @type {Object.<string, GeneratorFunction>}
 *
 * @see {@link module:modules/dealerConfiguration/sagas/index~initializeDealerTab} - Consumer of this dictionary
 */
export default {
  administration: administrationInitializer,
  afterSales: afterSalesInitializer,
  selfService: selfServiceInitializer,
  parts: partsInitializer,
  sales: salesInitializer,
  audit: auditModelInitializer,
  keyDeposit: keyDepositInitializer,
  customisation: customisationInitializer,
  integration: integrationInitializer,
};
